import { useForm } from 'react-final-form';
import styles from './InvestmentsGrid.module.scss';
import { getIn } from 'final-form';
import { useEffect, useState } from 'react';
import { getInitialInvestmentRounds, getInvestmentsGridInvalidRange, investmentsColumns, investmentsHeaders, investmentsHeadersObj, onAddInitialInvestmentRow } from './investementHelper';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import { NonEditableCell, ReactGrid } from '@silevis/reactgrid';
import { shortcutType } from '../components/ReactGrid/reactGridHelper';
import { CustomTextCell } from '../components/ReactGrid/CustomTextCell';
import { CustomNumberCell } from '../components/ReactGrid/CustomNumberCell';
import { DateCell } from '../components/ReactGrid/CustomDateCell';
import _ from 'lodash';
import { revenueGridStyles } from '../Revenue Details/revenueGridStyles';
import { CustomSelectCell } from '../components/ReactGrid/CustomSelectCell';
import { FieldMode, underwritingFormWarningType } from '../../../interfaces/interfaces';
import ShortcutModal from '../components/ReactGrid/ShortcutModal';
import { RiAddFill as AddIcon } from 'react-icons/ri'
import { Modal } from 'reactstrap';
import { BsFillExclamationCircleFill as WarningIcon } from 'react-icons/bs';
import { MdOutlineEdit as EditIcon } from 'react-icons/md';

type Props = {
    fieldName: string;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    fieldMode?: FieldMode;
    setFieldMode?: (value: FieldMode) => void;
    onChangeDetected?: () => void;
}

const getRows = (gridData) => {
    if (!gridData?.rowData?.length) return [];

    const headerRow = {
        rowIndex: 0,
        height: 42, // Explicitly setting height for headers
    };

    const dataRows = gridData.rowData.map((_, rowIndex) => ({
        rowIndex: rowIndex + 1, // Adjusting index for data rows
        height: 42,
    }));

    const footerRow = {
        rowIndex: gridData.rowData.length + 1, // Footer comes after last data row
        height: 42,
    };

    return [headerRow, ...dataRows, footerRow]; // Include header, data, and footer rows
};

const generateCells = (gridData, updateCellValue, addRow, deleteRow, fieldMode) => {
    // console.log('gridData', gridData);
    if (!gridData?.rowData?.length) return [];

    const headerCells = investmentsHeaders.map((header, colIndex) => {
        const headersObj = investmentsHeadersObj[header];
        return {
            rowIndex: 0,
            colIndex,
            isSelectable: false,
            isFocusable: false,
            Template: NonEditableCell,
            props: {
                value: headersObj.title,
                style: {
                    backgroundColor: "#F9F9F9",
                    paddingTop: "0.75rem",
                    paddingLeft: "0.5rem",
                    paddingRight: "0.5rem",
                    paddingBottom: "0.75rem",
                    ...headersObj.styles,
                }
            },
        }
    });
    const dataCells = gridData.rowData.flatMap((row, rowIndex) => {
        return investmentsHeaders.map((headerKey, colIndex) => {
            const headersObj = investmentsHeadersObj[headerKey] as any;
            const cellValue = row[headerKey];

            const allowShortcuts = gridData.rowData.length > 1 ? [shortcutType.addRow, shortcutType.deleteRow] : [shortcutType.addRow];
            if (fieldMode == FieldMode.view) {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    isSelectable: true,
                    isFocusable: true,
                    Template: NonEditableCell,
                    props: {
                        value: typeof (cellValue) === 'object' && cellValue !== null
                            ? cellValue.label || cellValue.value || JSON.stringify(cellValue)
                            : cellValue,
                        style: {
                            backgroundColor: "#F9F9F9",
                            paddingTop: "0.75rem",
                            paddingLeft: "0.5rem",
                            paddingRight: "0.5rem",
                            paddingBottom: "0.75rem",
                            ...headersObj.styles,
                        }
                    },
                }
            } else if (headersObj?.type == "string") {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: CustomTextCell,
                    props: {
                        text: cellValue,
                        onTextChanged: (newText) => updateCellValue(rowIndex, headerKey, newText),
                        onAddRow: (newRowIndex) => addRow(newRowIndex),
                        onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
                        allowShortcuts: allowShortcuts,
                        style: {
                            paddingTop: "0.75rem",
                            paddingLeft: "0.5rem",
                            paddingRight: "0.5rem",
                            paddingBottom: "0.75rem",
                            ...headersObj.styles,
                        }
                    },
                }
            } else if (headersObj?.type == "number") {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: CustomNumberCell,
                    props: {
                        value: cellValue,
                        onValueChanged: (newValue) => updateCellValue(rowIndex, headerKey, newValue),
                        onAddRow: (newRowIndex) => addRow(newRowIndex),
                        onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
                        allowShortcuts: allowShortcuts,
                        format: headersObj?.format || null,
                        style: {
                            paddingTop: "0.75rem",
                            paddingLeft: "0.5rem",
                            paddingRight: "0.5rem",
                            paddingBottom: "0.75rem",
                            ...headersObj.styles,
                        }
                    },
                }
            } else if (headersObj?.type == "date") {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: DateCell,
                    props: {
                        value: cellValue,
                        onValueChanged: (newValue) => updateCellValue(rowIndex, headerKey, newValue),
                        onAddRow: (newRowIndex) => addRow(newRowIndex),
                        onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
                        allowShortcuts: allowShortcuts,
                        style: {
                            paddingTop: "0.75rem",
                            paddingLeft: "0.5rem",
                            paddingRight: "0.5rem",
                            paddingBottom: "0.75rem",
                            ...headersObj.styles,
                        }
                    },
                }
            } else if (headersObj?.type == "select") {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: CustomSelectCell,
                    props: {
                        value: cellValue,
                        onValueChanged: (newValue) => updateCellValue(rowIndex, headerKey, newValue),
                        onAddRow: (newRowIndex) => addRow(newRowIndex),
                        onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
                        options: headersObj?.options,
                        optionLabelKey: headersObj?.optionLabelKey,
                        allowShortcuts: allowShortcuts,
                        style: {
                            paddingTop: "0.75rem",
                            paddingLeft: "0.5rem",
                            paddingRight: "0.5rem",
                            paddingBottom: "0.75rem",
                            ...headersObj.styles,
                        }
                    }
                }
            } else if (headersObj?.type == "noneditable") {
                return {
                    rowIndex: rowIndex + 1,
                    colIndex,
                    Template: CustomNumberCell,
                    props: {
                        value: cellValue,
                        onAddRow: (newRowIndex) => addRow(newRowIndex),
                        onDeleteRow: (newRowIndex) => deleteRow(newRowIndex),
                        allowShortcuts: allowShortcuts,
                        style: {
                            paddingTop: "0.75rem",
                            paddingLeft: "0.5rem",
                            paddingRight: "0.5rem",
                            paddingBottom: "0.75rem",
                            ...headersObj.styles,
                        }
                    }
                }
            }

            // console.log('coming here', colIndex)
        })
    });

    const footerCells = investmentsHeaders.map((headerKey, colIndex) => {
        const headersObj = investmentsHeadersObj[headerKey];
        const cellValue = gridData.footer[headerKey]

        // Format the value if it's a number and has a format specified
        let displayValue = cellValue;
        if (headersObj?.format && typeof cellValue === 'number') {
            displayValue = headersObj.format.format(cellValue);
        }

        return {
            rowIndex: gridData.rowData.length + 1,
            colIndex,
            isSelectable: true,
            isFocusable: true,
            Template: NonEditableCell,
            props: {
                value: displayValue,
                style: {
                    backgroundColor: "#F9F9F9",
                    paddingTop: "0.75rem",
                    paddingLeft: "0.5rem",
                    paddingRight: "0.5rem",
                    paddingBottom: "0.75rem",
                    fontWeight: "600",
                    ...headersObj.styles,
                }
            },
        }
    });

    // console.log('headerCells', headerCells);
    // console.log('dataCells', dataCells);
    // console.log('footerCells', footerCells);
    // console.log('footerCells', footerCells);

    return [...headerCells, ...dataCells, ...footerCells];
}

const InvestmentsGrid = ({ fieldName, isOpen, setIsOpen, fieldMode = FieldMode.edit, setFieldMode = () => { }, onChangeDetected = () => { } }: Props) => {
    const { getState, change } = useForm();

    const investmentRounds = getIn(getState()?.values, `${fieldName}`);

    const [gridData, setGridData] = useState({
        headers: [],
        rowData: [],
        footer: {}
    });

    const [validation, setValidation] = useState({
        message: '',
        styledRange: []
    });

    const [initialGridData, setInitialGridData] = useState(null);
    const [isShorcutModalOpen, setIsShorcutModalOpen] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const [warning, setWarning] = useState({
        state: false,
        type: null
    });

    useEffect(() => {
        if (isOpen) {
            if (!investmentRounds) {
                const investmentRoundsGridData = getInitialInvestmentRounds();
                console.log('investmentRoundsGridData', investmentRoundsGridData);
                setGridData(investmentRoundsGridData);
                setInitialGridData(_.cloneDeep({
                    headers: investmentRoundsGridData.headers,
                    rowData: investmentRoundsGridData.rowData,
                    footer: investmentRoundsGridData.footer
                }))
            } else {
                setInitialGridData(_.cloneDeep(investmentRounds));
                setGridData(investmentRounds);
            }
        }

    }, [isOpen]);

    const addRow = (rowIndex: number) => {
        setGridData((prev) => {
            const newGridData = _.cloneDeep(prev?.rowData);
            newGridData.splice(rowIndex, 0, onAddInitialInvestmentRow());
            const addedRoundNumber = newGridData.map((item, index) => {
                // return [index + 1, ...item.slice(1)];
                return {
                    ...item,
                    [investmentsColumns.round]: index + 1,
                }
            });


            return {
                ...prev,
                rowData: addedRoundNumber,
            };
        });
    }

    const updateCellValue = (rowIndex: string, columnKey: string, value) => {
        setGridData((prev) => {
            // console.log('path', path, 'value', value);
            const newGridData = _.cloneDeep(prev?.rowData);
            _.set(newGridData, `${rowIndex}.${columnKey}`, value);

            const footerData = _.cloneDeep(prev?.footer);

            // const [rowIndex, colIndex] = path.match(/\d+/g).map(Number);
            // calculate row sum 
            const headersObj = investmentsHeadersObj[columnKey];
            if (headersObj?.isTotal) {
                const sum = newGridData.reduce((acc, row) => {
                    return acc + (row[columnKey] || 0);
                }, 0);
                // newGridData[newGridData.length - 1][colIndex] = sum;
                if (newGridData.length == 1 && !sum) footerData[columnKey] = "-";
                else footerData[columnKey] = sum;
            }
            return {
                ...prev,
                rowData: newGridData,
                footer: footerData,
            };
        })
    }

    const deleteRow = (rowIndex: number) => {
        // console.log('rowIndex', rowIndex);
        setGridData((prev) => {
            const newGridData = _.cloneDeep(prev?.rowData);
            newGridData.splice(rowIndex, 1);
            // calculate footer sum
            // const footerData = new Array(prev.headers.length).fill(0);

            const footerData = {};
            investmentsHeaders.forEach((headerKey, colIndex) => {
                footerData[headerKey] = 0;
            });

            newGridData.forEach((row, rowIndex) => {
                investmentsHeaders.forEach((headerKey, colIndex) => {
                    const cellValue = row[headerKey];
                    const headersObj = investmentsHeadersObj[headerKey];
                    if (headersObj?.isTotal) {
                        const sumValue = (footerData[headerKey] || 0) + (cellValue || 0);
                        if (newGridData?.length == 1 && !sumValue) footerData[headerKey] = "-";
                        else footerData[headerKey] = sumValue;
                    } else {
                        if (colIndex == 0) footerData[headerKey] = "Total";
                        else footerData[headerKey] = "-";
                    }
                });
            });

            const addedRoundNumber = newGridData.map((item, index) => {
                return {
                    [investmentsColumns.round]: index + 1,
                    ...item,
                }
            });


            return {
                ...prev,
                rowData: addedRoundNumber,
                footer: footerData,
            }
        });
    }

    const columns = investmentsHeaders.map((header, colIndex) => {
        const headersObj = investmentsHeadersObj[header];
        // console.log('headersObj', headersObj);
        return {
            colIndex: colIndex,
            width: headersObj.width,
        }
    });

    const rows = getRows(gridData);
    const cells = generateCells(gridData, updateCellValue, addRow, deleteRow, fieldMode);

    const validateGrid = () => {
        const { missingDataErrors, dateErrors, fullGridError } = getInvestmentsGridInvalidRange(gridData);
        console.log('missingDataErrors', missingDataErrors);
        console.log('dateErrors', dateErrors);
        console.log('fullGridError', fullGridError);
        if (fullGridError.length) {
            setValidation({
                message: 'Enter at least 1 investment round',
                styledRange: fullGridError
            })
        } else if (missingDataErrors.length) {
            setValidation({
                message: "Some cells are missing data.",
                styledRange: missingDataErrors
            });
        } else if (dateErrors.length) {
            setValidation({
                message: "Investment Date field contain invalid values.",
                styledRange: dateErrors
            });
        } else {
            setValidation({ message: "", styledRange: [] });
        }

        return missingDataErrors.length === 0 && dateErrors.length === 0 && fullGridError.length == 0; // Returns true if grid is valid
    }

    const handelChangeDetected = () => {
        const stringifiedPrevData = JSON.stringify(initialGridData);
        const stringifiedNewData = JSON.stringify(gridData);
        if (stringifiedPrevData !== stringifiedNewData) {
            onChangeDetected();
        }
    }


    return (
        <div className={styles.rootContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.leftContainer}>
                    Investments
                </div>
                <div className={styles.rightContainer}>
                    <MeritoButton
                        btnText={fieldMode == FieldMode.view ? "Edit" : "Save"}
                        type='primary-1'
                        icon={fieldMode == FieldMode.view ? <EditIcon /> : null}
                        onClick={() => {
                            // save the provisional statement
                            if (fieldMode == FieldMode.view) {
                                setFieldMode(FieldMode.edit);
                                return;
                            }
                            const isValid = validateGrid();
                            setIsSubmitted(true);
                            if (isValid) {
                                change(fieldName, gridData);
                                handelChangeDetected();
                                setIsOpen(false);
                            } else {
                                setWarning({
                                    state: true,
                                    type: underwritingFormWarningType.save
                                })
                            }
                        }}
                    />
                    <MeritoButton
                        btnText="Cancel"
                        type='secondary-1'
                        onClick={() => {
                            // setIsOpen(false);
                            // setIsOpen(false)
                            const stringifiedPrevData = JSON.stringify(initialGridData);
                            const stringifiedNewData = JSON.stringify(gridData);
                            if (stringifiedPrevData !== stringifiedNewData) {
                                setWarning({
                                    state: true,
                                    type: underwritingFormWarningType.cancel
                                });
                            } else {
                                setIsOpen(false)
                            }
                        }}
                    />
                </div>
            </div>
            <div className={styles.tipsContainer}>
                <div className={styles.tipsTitle}>
                    Tips:
                </div>
                <div className={styles.tipsContent}>
                    <ol>
                        <li>Enter absolute values in ₹.</li>
                        <li>You can use these <span onClick={() => {
                            setIsShorcutModalOpen(true)
                        }}>spreadsheet shortcuts.</span></li>
                    </ol>
                </div>
            </div>
            <div className={styles.tableRootContainer}>
                {gridData?.rowData?.length ? <ReactGrid
                    id="investmentRoundsGrid"
                    columns={columns}
                    rows={rows}
                    cells={cells}
                    stickyTopRows={1}
                    stickyBottomRows={1}
                    styledRanges={validation.styledRange}
                    styles={revenueGridStyles?.rgStyles}
                /> : null}
            </div>

            {validation.message ? <div className={styles.errorMessage}>
                {validation.message}
            </div> : null}
            <div className={styles.btnContainer}>
                <MeritoButton
                    type="secondary-1"
                    size="md"
                    disabled={fieldMode == FieldMode.view}
                    icon={<AddIcon />}
                    btnText="Insert Row"
                    onClick={() => {
                        if (fieldMode == FieldMode.view) return;
                        addRow(gridData.rowData.length);
                    }}
                />
            </div>

            <Modal
                size={"md"}
                isOpen={warning?.state}
                toggle={() => {
                    setWarning({ state: false, type: null });
                }}
                centered={true}
                style={{ width: '450px' }}
                contentClassName='shortcut_modal'
            >
                <div className={styles.confirmationRootContainer}>
                    <div className={styles.topContainer}>
                        <div className={styles.iconContainer}>
                            <WarningIcon />
                        </div>
                        <div className={styles.messageContainer}>
                            {warning?.type === underwritingFormWarningType.save ? 'Required Details Are Missing!' : 'Unsaved Changes Will Be Lost!'}
                        </div>
                        {warning?.type == underwritingFormWarningType.save ? <div className={styles.subMessageContainer}>
                            Do you want to proceed anyway?
                        </div> : null}
                    </div>

                    <div className={styles.bottomContainer}>
                        {warning?.type === underwritingFormWarningType.save ?
                            <>
                                <MeritoButton
                                    btnText={'Save & Continue'}
                                    size='lg'
                                    isRounded={true}
                                    type='secondary-2'
                                    onClick={() => {
                                        change(fieldName, gridData);
                                        setWarning({
                                            state: false,
                                            type: null
                                        })
                                        setIsOpen(false);
                                    }}
                                />
                                <MeritoButton
                                    btnText="Continue Editing"
                                    size='lg'
                                    isRounded={true}
                                    type='primary-1'
                                    onClick={() => {
                                        setWarning({
                                            state: false,
                                            type: null
                                        });
                                    }}
                                />
                            </>
                            :
                            <>
                                <MeritoButton
                                    btnText={'Delete Changes'}
                                    size='lg'
                                    isRounded={true}
                                    type='secondary-2'
                                    onClick={() => {
                                        setWarning({
                                            state: false,
                                            type: null
                                        })
                                        setIsOpen(false);
                                    }}
                                />
                                <MeritoButton
                                    btnText="Continue Editing"
                                    size='lg'
                                    isRounded={true}
                                    type='primary-1'
                                    onClick={() => {
                                        setWarning({
                                            state: false,
                                            type: null
                                        });
                                    }}
                                />
                            </>
                        }

                    </div>
                </div>
            </Modal>


            <ShortcutModal isOpen={isShorcutModalOpen} setIsOpen={setIsShorcutModalOpen}
                showShortcuts={[shortcutType.addRow, shortcutType.deleteRow, shortcutType.cut, shortcutType.copy, shortcutType.paste, shortcutType.moveLeft, shortcutType.moveRight]} />
        </div>
    );
}

export default InvestmentsGrid;